import * as signalR from "@microsoft/signalr";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import apiClient from "../api/apiClient";
import ENDPOINTS from "../api/endPoint";
import { useAuth } from "./AuthContext";

const SignalRContext = createContext();

export const SignalRProvider = ({ children, connectionUrl }) => {
  const { user } = useAuth();
  const [connection, setConnection] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState({ totalCount: 0, chatCounts: [] });
  const [activeChats, setActiveChats] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const currentChatIdRef = useRef(currentChatId);
  useEffect(() => { currentChatIdRef.current = currentChatId; }, [currentChatId]);
  // Add these states to your SignalRProvider
  const [departmentNotifications, setDepartmentNotifications] = useState({});
  // Initialize connection
  useEffect(() => {
    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(connectionUrl, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.elapsedMilliseconds < 60000) {
            return 2000 + Math.random() * 2000;
          }
          return 60000;
        },
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    setConnection(newConnection);

    return () => {
      if (newConnection) {
        newConnection.stop();
      }
    };
  }, [connectionUrl]);

  // Setup connection handlers
  useEffect(() => {
    if (!connection) return;

    const startConnection = async () => {
      try {
        await connection.start();
        setIsConnected(true);
        console.log("SignalR Connected");
        // Register handlers
        connection.on("ReceiveNotification", handleNewNotification);
        connection.on("ReceiveChatNotification", handleChatNotification);
        connection.on("ReceiveMessage", handleNewChatMessage);
        connection.onclose(() => setIsConnected(false));
        connection.onreconnected(() => setIsConnected(true));
      } catch (err) {
        console.error("SignalR Connection Error:", err);
        setTimeout(startConnection, 5000);
      }
    };

    startConnection();

    return () => {
      connection.off("ReceiveNotification", handleNewNotification);
      connection.off("ReceiveChatNotification", handleChatNotification);
      connection.off("ReceiveMessage", handleNewChatMessage);
    };
  }, [connection]);

  // 🔄 NEW useEffect to invoke methods after user and connection are ready
  useEffect(() => {
    const fetchInitialNotifications = async () => {
      if (user?.id && isConnected && connection) {
        try {
          const totalUnread = await connection.invoke(
            "GetTotalUnreadCount",
            parseInt(user.id),
            user.role
          );

          const unseenNotifications = await connection.invoke(
            "GetUnseenNotifications",
            parseInt(user.id),
            user.role
          );

          setNotifications(unseenNotifications || []);
          setNotificationsUnreadCount(unseenNotifications?.length || 0);
          setUnreadCount(totalUnread || { totalCount: 0, chatCounts: [] });
        } catch (invokeErr) {
          console.error("Error getting initial notifications:", invokeErr);
        }
      }
    };

    fetchInitialNotifications();
  }, [user, isConnected, connection]);

  const handleNewNotification = (notification) => {
    if (user?.id != notification?.recipientId) return;

    setNotifications((prev) => {
      const alreadyExists = prev.some(
        (n) => n.notification_Id === notification.notification_Id
      );

      if (alreadyExists) return prev;

      // If it's a new notification, update state
      setNotificationsUnreadCount((count) => count + 1);

      return [
        {
          ...notification,
          id: Date.now(),
          isRead: false,
          timestamp: new Date().toLocaleString(),
        },
        ...prev,
      ];
    });
  };

  const handleChatNotification = (notification) => {
    // Use ref to get current chatId (avoids stale closure)
    const isActiveChat = currentChatIdRef.current === notification.chatId;
    if (
      !notification ||
      notification.senderId === parseInt(user?.id) ||
      isActiveChat
    )
      return;

    setUnreadCount((prev) => {
      const existing = prev.chatCounts?.find(
        (c) => c.chatId === notification.chatId
      );

      let updatedChatCounts;
      if (existing) {
        updatedChatCounts = prev.chatCounts.map((c) =>
          c.chatId === notification.chatId ? { ...c, count: c.count + 1 } : c
        );
      } else {
        updatedChatCounts = [
          ...(prev.chatCounts || []),
          {
            chatId: notification.chatId,
            count: 1,
            departmentId: notification.departmentId || null,
          },
        ];
      }

      return {
        ...prev,
        chatCounts: updatedChatCounts,
        totalCount: (prev.totalCount || 0) + 1,
      };
    });
  };

  // Add a function to clear department notifications
  const clearDepartmentNotifications = (departmentId) => {
    setDepartmentNotifications((prev) => ({
      ...prev,
      [departmentId]: 0,
    }));
  };

  const handleNewChatMessage = async (message) => {
    // Unread count is handled ONLY by handleChatNotification (ReceiveChatNotification)
    // This handler only updates the active chat messages UI

    setActiveChats((prev) => {
      const existingChat = prev[message.chatId];
      if (!existingChat) return prev;

      // Deduplicate — ReceiveMessage fires from both chat and department groups
      const alreadyExists = existingChat.messages?.some(
        (m) => m.messageId === message.messageId
      );
      if (alreadyExists) return prev;

      return {
        ...prev,
        [message.chatId]: {
          ...existingChat,
          messages: [
            ...(existingChat.messages || []),
            { ...message, messageText: message?.message },
          ],
        },
      };
    });
  };

  const markAsRead = (id) => {
    connection
      .invoke("MarkNotificationAsSeen", id)
      .catch((err) => console.error(err));
    // Remove the notification from the list
    setNotifications((prev) => prev.filter((n) => n.notification_Id !== id));
    setNotificationsUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotificationsUnreadCount(0);
  };

  const startNewChat = async (userId, departmentId, isCustomer = true) => {
    if (!isConnected || !connection) {
      throw new Error("Connection not established");
    }

    try {
      const response = await apiClient(ENDPOINTS.CHAT.START, {
        method: "POST",
        body: {
          [isCustomer ? "customerId" : "userId"]: userId,
          departmentId,
        },
      });

      const chatId = response?.chatId || response?.id;
      if (!chatId) throw new Error("No chatId returned from API");
      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(
          `${connectionUrl}?chatId=${userId}&departmentId=${departmentId}`,
          {
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
          }
        )
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.elapsedMilliseconds < 60000) {
              return 2000 + Math.random() * 2000;
            }
            return 60000;
          },
        })
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      setConnection(newConnection);
      return chatId;
    } catch (err) {
      console.error("Error starting chat:", err);
      throw err;
    }
  };

  const sendChatMessage = async (chatId, senderId, senderType, message) => {
    if (!isConnected || !connection) {
      throw new Error("Connection not established");
    }
    try {
      await connection.invoke(
        "SendMessage",
        chatId,
        parseInt(senderId),
        senderType,
        message
      );
    } catch (err) {
      console.error("Error sending chat message:", err);
      throw err;
    }
  };

  const joinDepartmentGroup = async (departmentId) => {
    if (!isConnected || !connection) return;

    try {
      // await connection.invoke("AddToGroup", `department_${departmentId}`);
    } catch (err) {
      console.error("Error joining department group:", err);
    }
  };

  const fetchCustomerChats = async (departmentId) => {
    try {
      const response = await apiClient(
        `/Chat/department/${departmentId}/customers-with-chats`
      );
      return response;
    } catch (err) {
      console.error("Error fetching customer chats:", err);
      throw err;
    }
  };

  const value = useMemo(
    () => ({
      connection,
      isConnected,
      notifications,
      unreadCount,
      activeChats,
      currentChatId,
      departmentNotifications,
      notificationsUnreadCount,
      // connection,
      setUnreadCount,
      setNotificationsUnreadCount,
      setCurrentChatId,
      markAsRead,
      markAllAsRead,
      startNewChat,
      sendChatMessage,
      joinDepartmentGroup,
      fetchCustomerChats,
      setActiveChats,
      clearDepartmentNotifications,
    }),
    [
      connection,
      isConnected,
      notifications,
      unreadCount,
      activeChats,
      currentChatId,
      departmentNotifications,
      notificationsUnreadCount,
      setUnreadCount,
      setNotificationsUnreadCount,
      setCurrentChatId,
      markAsRead,
      markAllAsRead,
      startNewChat,
      sendChatMessage,
      joinDepartmentGroup,
      fetchCustomerChats,
      setActiveChats,
      clearDepartmentNotifications,
    ]
  );

  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
};

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error("useSignalR must be used within a SignalRProvider");
  }
  return context;
};
